import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";

import { useParceiro } from "../../pages/GestaoParceirosPage/hooks/useParceiro";
import { useTurmaResponsavel } from "./hooks/useTurmaResponsavel";
import { useAppFeedback } from "hooks/useAppFeedback";
import { useAuth } from "resources/AuthProvider";
import SearchSelect from "../../atoms/SearchSelect/SearchSelect";
import MultiSelectCheck from "components/atoms/MultiSelectField/MultSelectCheck";
import { tipoResponsavelTurma } from "resources/Dados";
import styles_input from "../../../styles/Input.module.css";
import styles_grid from "../../../styles/Grid.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_button from "../../../styles/Button.module.css";

interface TurmaResponsaveisFormProps {
  onSaveSuccess?: () => void;
  hideSaveButton?: boolean;
  onRegisterSave?: (saveHandler: () => Promise<boolean>) => void;
}

const TurmaResponsaveisForm = ({
  onSaveSuccess,
  hideSaveButton = false,
  onRegisterSave,
}: TurmaResponsaveisFormProps) => {
  const [searchParams] = useSearchParams();
  const turmaId = searchParams.get("id");
  const { showError, showSuccess } = useAppFeedback();
  const { hasActionPermission } = useAuth();

  const { parceirosResumo, buscarParceirosResumo } = useParceiro();
  const canViewUsuarios =
    hasActionPermission("usuarios.view_all") || hasActionPermission("usuarios.view_own");
  const canViewInstituicoes = hasActionPermission("instituicoes.view_all");
  const tipoResponsavelOptions = tipoResponsavelTurma.filter(({ id }) => {
    if (id === 1) return canViewUsuarios;
    if (id === 2) return canViewInstituicoes;
    return false;
  });

  const {
    msgError,
    msgSuccess,
    loadingResp,
    loadingSave,
    loadingUsers,
    tipoRespId,
    setTipoRespId,
    institutionId,
    setInstitutionId,
    selectedUsersLotacao,
    selectedUsersInstituicao,
    usersLotacao,
    usersInstituicao,
    handleUsersAreaChange,
    handleUsersInstChange,
    saveTurmaResp,
  } = useTurmaResponsavel(turmaId);

  useEffect(() => {
    if (msgSuccess) {
      showSuccess(msgSuccess);
    }
  }, [msgSuccess, showSuccess]);

  useEffect(() => {
    if (msgError) {
      showError(msgError);
    }
  }, [msgError, showError]);

  useEffect(() => {
    onRegisterSave?.(saveTurmaResp);
  }, [onRegisterSave, saveTurmaResp]);

  if (loadingResp) {
    return <CircularProgress />;
  }

  return (
    <div className={`${styles_general.mt2} ${styles_general.p2} ${styles_grid.grid12}`}>
      <div className={styles_grid.inputGroup4}>
        <label className={styles_input.titleInput}>Tipo</label>
        <SearchSelect
          label=""
          key="tipo_responsavel_id"
          options={tipoResponsavelOptions}
          value={tipoRespId}
          onChange={(id) => {
            setTipoRespId(id);
            if (id === 2) {
              buscarParceirosResumo();
            }
          }}
          paperClassname={styles_input.paper}
          popperClassname={styles_input.popper}
          labelClassname={styles_input.labelSelect}
          inputClassname={styles_input.inputSelect}
        />
      </div>

      {tipoRespId == 1 && (
        <div className={styles_grid.inputGroupFull}>
          {loadingUsers && <CircularProgress />}
          {usersLotacao.length > 0 && (
            <MultiSelectCheck
              label="Usuários Responsáveis Técnicos*"
              placeholder="Selecione no mínimo um usuário"
              fullWidth
              value={selectedUsersLotacao.map((d) => d)}
              onChange={handleUsersAreaChange}
              options={usersLotacao}
              required
            />
          )}
        </div>
      )}

      {tipoRespId == 2 && (
        <>
          <div className={styles_grid.inputGroup8}>
            <label className={styles_input.titleInput}>Instituição Responsável</label>
            <SearchSelect
              label=""
              key="institution_id"
              options={parceirosResumo}
              value={institutionId}
              onChange={(id) => {
                setInstitutionId(id);
              }}
              paperClassname={styles_input.paper}
              popperClassname={styles_input.popper}
              labelClassname={styles_input.labelSelect}
              inputClassname={styles_input.inputSelect}
            />
          </div>

          <div className={styles_grid.inputGroupFull}>
            {loadingUsers && <CircularProgress />}
            {usersInstituicao.length > 0 && (
              <MultiSelectCheck
                label="Usuário(s) da Instituição*"
                placeholder="Selecione no mínimo um usuário"
                fullWidth
                value={selectedUsersInstituicao.map((d) => d)}
                onChange={handleUsersInstChange}
                options={usersInstituicao}
                required
              />
            )}
          </div>
        </>
      )}

      {!hideSaveButton && (
        <div className={`${styles_grid.inputGroupFull} ${styles_grid.justifyCenter} ${styles_general.mt2}`}>
          <Button
            variant="contained"
            onClick={async () => {
              const success = await saveTurmaResp();
              if (success) {
                onSaveSuccess?.();
              }
            }}
            className={`${styles_button.buttonPrimary} ${loadingSave ? styles_button.buttonLoad : ""} `}
            color="primary"
          >
            {loadingSave ? (
              <>
                <div className={styles_button.spinner}></div>
                <span>Salvando...</span>
              </>
            ) : (
              <span>Salvar Responsáveis da Turma</span>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TurmaResponsaveisForm;
